// Deterministic calculation engines. All client-side, no API calls.

export const READINESS_METHOD = { version: "Scholavia Readiness Framework v1.0", updated: "2025-09" };

const clamp = (n, min = 0, max = 100) => Math.max(min, Math.min(max, Math.round(n)));

// ---------- READINESS ----------
// Returns overall + 5 domains, each with score, evidence, validation, benchmark, gap, action.
export function computeReadiness(profile = {}, ctx = {}) {
  const gpa = parseFloat(profile.gpa);
  const band = parseFloat(profile.languageScore);
  const targetBand = 6.5;
  const achievements = (profile.achievements || "").trim();
  const experience = (profile.experience || "").trim();
  const budget = profile.budget || "";
  const completedTasks = ctx.completedTasks || [];
  const testAttempts = ctx.testAttempts || [];

  // Academic
  const academicScore = isNaN(gpa) ? 0 : clamp((gpa / 4.0) * 100);
  const academic = {
    key: "academic", label: "Akademik", score: academicScore,
    evidence: isNaN(gpa) ? "GPA belum diisi" : `GPA ${gpa.toFixed(2)} / 4.00`,
    validation: isNaN(gpa) ? "UNKNOWN" : "SELF_REPORTED",
    benchmark: "Umumnya beasiswa top: GPA ≥ 3.0",
    gap: isNaN(gpa) ? "Lengkapi GPA di profil" : gpa >= 3.0 ? "Memenuhi ambang umum" : "Di bawah ambang umum",
    action: isNaN(gpa) ? "Isi GPA di profil" : gpa >= 3.5 ? "Pertahankan & lengkapi transkrip" : "Perkuat aspek lain untuk kompensasi",
  };

  // Language
  const languageScore = isNaN(band) ? 0 : clamp((band / 7.5) * 100);
  const langGap = isNaN(band) ? null : +(targetBand - band).toFixed(1);
  const language = {
    key: "language", label: "Bahasa", score: languageScore,
    evidence: isNaN(band) ? "Skor bahasa belum diisi" : `IELTS ${band}`,
    validation: isNaN(band) ? "UNKNOWN" : "SELF_REPORTED",
    benchmark: `Target umum: IELTS ${targetBand}`,
    gap: isNaN(band) ? "Lengkapi skor bahasa" : langGap > 0 ? `Kurang ${langGap} band dari target` : "Memenuhi target umum",
    action: isNaN(band) ? "Isi skor bahasa" : langGap > 0 ? "Latihan Reading & Vocabulary, lalu tes ulang" : "Pertahankan skor",
  };

  // Experience
  const expScore = clamp((experience ? 45 : 0) + (achievements ? 45 : 0) + (experience.length > 60 ? 10 : 0));
  const experienceDomain = {
    key: "experience", label: "Pengalaman", score: expScore,
    evidence: experience || achievements ? "Pengalaman/prestasi terisi" : "Belum ada data pengalaman",
    validation: experience || achievements ? "SELF_REPORTED" : "UNKNOWN",
    benchmark: "Beasiswa kepemimpinan menilai dampak nyata",
    gap: expScore >= 70 ? "Cukup kuat" : "Perlu bukti dampak yang lebih konkret",
    action: expScore >= 70 ? "Dokumentasikan dampak terukur" : "Bangun 1 inisiatif dengan hasil terukur",
  };

  // Financial (readiness/awareness, not ability guarantee)
  const budgetMap = { Limited: 55, Standard: 75, "Higher Comfort": 90 };
  const finScore = budgetMap[budget] ?? 40;
  const financial = {
    key: "financial", label: "Finansial", score: finScore,
    evidence: budget ? `Preferensi budget: ${budget}` : "Preferensi budget belum diisi",
    validation: budget ? "SELF_REPORTED" : "UNKNOWN",
    benchmark: "Fully-funded menutup sebagian besar biaya",
    gap: finScore >= 75 ? "Fleksibilitas cukup" : "Prioritaskan beasiswa fully-funded",
    action: "Gunakan Kalkulator Biaya untuk memetakan kebutuhan",
  };

  // Application readiness (docs + tasks done)
  const appScore = clamp(Math.min(100, completedTasks.length * 12 + testAttempts.length * 8));
  const application = {
    key: "application", label: "Aplikasi", score: appScore,
    evidence: `${completedTasks.length} tugas roadmap selesai, ${testAttempts.length} tes latihan`,
    validation: completedTasks.length ? "TRACKED" : "UNKNOWN",
    benchmark: "Dokumen inti lengkap sebelum deadline",
    gap: appScore >= 70 ? "Progres baik" : "Banyak langkah aplikasi belum dimulai",
    action: "Selesaikan langkah prioritas di Roadmap",
  };

  const domains = [academic, language, experienceDomain, financial, application];
  const weights = { academic: 0.25, language: 0.25, experience: 0.2, financial: 0.15, application: 0.15 };
  const overall = clamp(domains.reduce((sum, d) => sum + d.score * weights[d.key], 0));

  const filled = [gpa, band].filter((v) => !isNaN(v)).length + [experience || achievements, budget].filter(Boolean).length;
  const confidence = filled >= 4 ? "Medium-High" : filled >= 2 ? "Medium" : "Low";

  return { overall, domains, confidence, method: READINESS_METHOD, langGap };
}

// ---------- MATCHING ENGINE ----------
// Eligibility states: PASS | FAIL | UNKNOWN | NOT_APPLICABLE. UNKNOWN never becomes PASS.
export function computeMatch(profile = {}, scholarship) {
  const gpa = parseFloat(profile.gpa);
  const band = parseFloat(profile.languageScore);
  const expYears = parseFloat(profile.experienceYears);
  const prefCountries = (profile.countries || "").toLowerCase();
  const field = (profile.field || "").toLowerCase();
  const degree = (profile.degreeTarget || "").toLowerCase();

  const dims = [];
  const push = (key, label, state, detail) => dims.push({ key, label, state, detail });

  // 1 Citizenship
  if (scholarship.citizenship === "Indonesia only" || scholarship.citizenship === "Indonesia eligible") {
    push("citizenship", "Kewarganegaraan", "PASS", "Terbuka untuk WNI.");
  } else {
    push("citizenship", "Kewarganegaraan", "PASS", "Terbuka untuk kandidat internasional.");
  }

  // 2 Degree
  if (!degree) push("degree", "Jenjang", "UNKNOWN", "Target jenjang belum diisi di profil.");
  else if (degree.includes(scholarship.degree.toLowerCase())) push("degree", "Jenjang", "PASS", `Cocok untuk ${scholarship.degree}.`);
  else push("degree", "Jenjang", "FAIL", `Beasiswa ini untuk ${scholarship.degree}, targetmu berbeda.`);

  // 3 Academic
  if (isNaN(gpa)) push("academic", "Akademik", "UNKNOWN", "GPA belum diisi.");
  else if (gpa >= scholarship.minGpa) push("academic", "Akademik", "PASS", `GPA ${gpa} ≥ minimum ${scholarship.minGpa}.`);
  else push("academic", "Akademik", "FAIL", `GPA ${gpa} di bawah minimum ${scholarship.minGpa}.`);

  // 4 Language
  if (isNaN(band)) push("language", "Bahasa", "UNKNOWN", "Skor bahasa belum diisi.");
  else if (band >= scholarship.language.min) push("language", "Bahasa", "PASS", `IELTS ${band} ≥ ${scholarship.language.min}.`);
  else push("language", "Bahasa", "FAIL", `IELTS ${band} < ${scholarship.language.min} (gap ${(scholarship.language.min - band).toFixed(1)} band).`);

  // 5 Experience
  if (scholarship.experienceYears === 0) push("experience", "Pengalaman", "NOT_APPLICABLE", "Tidak ada syarat pengalaman minimum.");
  else if (isNaN(expYears)) push("experience", "Pengalaman", "UNKNOWN", "Lama pengalaman belum diisi.");
  else if (expYears >= scholarship.experienceYears) push("experience", "Pengalaman", "PASS", `${expYears} thn ≥ ${scholarship.experienceYears} thn.`);
  else push("experience", "Pengalaman", "FAIL", `Butuh ${scholarship.experienceYears} thn pengalaman.`);

  // 6 Field
  if (!field) push("field", "Bidang", "UNKNOWN", "Bidang belum diisi.");
  else if (scholarship.field.some((f) => f.toLowerCase() === "any" || field.includes(f.toLowerCase()) || f.toLowerCase().includes(field)))
    push("field", "Bidang", "PASS", "Bidangmu didukung.");
  else push("field", "Bidang", "UNKNOWN", "Kecocokan bidang perlu diverifikasi ke penyelenggara.");

  // 7 Career alignment (soft, informational)
  push("career", "Kesesuaian Karier", "UNKNOWN", "Dinilai kualitatif lewat esai — tidak dihitung otomatis.");

  // 8 Timeline
  if (!scholarship.deadline) push("timeline", "Timeline", "UNKNOWN", "Deadline belum terverifikasi.");
  else {
    const days = Math.round((new Date(scholarship.deadline) - new Date()) / 86400000);
    if (days > 0) push("timeline", "Timeline", "PASS", `Masih ${days} hari menuju deadline.`);
    else push("timeline", "Timeline", "FAIL", "Deadline siklus ini sudah lewat.");
  }

  // 9 Financial relevance
  if (scholarship.funding === "Fully Funded") push("financial", "Relevansi Finansial", "PASS", "Fully-funded — relevan untuk budget terbatas.");
  else push("financial", "Relevansi Finansial", "UNKNOWN", "Cakupan pendanaan perlu dicek.");

  // 10 Document readiness
  push("documents", "Kesiapan Dokumen", "UNKNOWN", "Dilacak lewat Roadmap — lengkapi dokumen inti.");

  // Score: PASS counts, weighted; NOT_APPLICABLE excluded; UNKNOWN & FAIL contribute 0 (FAIL penalises)
  const scoreable = dims.filter((d) => d.state !== "NOT_APPLICABLE");
  const passes = scoreable.filter((d) => d.state === "PASS").length;
  const fails = scoreable.filter((d) => d.state === "FAIL").length;
  let matchScore = clamp((passes / scoreable.length) * 100 - fails * 8);

  return { matchScore, dims, passes, fails, unknowns: scoreable.filter((d) => d.state === "UNKNOWN").length };
}

// ---------- COST ----------
export function computeCost({ city, months = 12, lifestyle = "Standard", oneTime, includeTuition = true }) {
  const mult = { Budget: 0.8, Standard: 1.0, "Higher Comfort": 1.35 }[lifestyle] ?? 1;
  const monthlyItems = {
    housing: Math.round(city.housing * mult),
    food: Math.round(city.food * mult),
    transport: Math.round(city.transport * mult),
    personal: Math.round(city.personal * mult),
    utilities: Math.round(city.utilities * mult),
    insurance: Math.round(city.insurance),
  };
  const monthly = Object.values(monthlyItems).reduce((a, b) => a + b, 0);
  const annualLiving = monthly * 12;
  const totalLiving = monthly * months;
  const tuitionTotal = includeTuition ? Math.round((city.tuitionYear || 0) * (months / 12)) : 0;
  const oneTimeTotal = oneTime ? oneTime.visa + oneTime.flight + oneTime.setup : 0;
  const emergency = Math.round(monthly * 1);
  const total = totalLiving + tuitionTotal + oneTimeTotal + emergency;
  return { monthlyItems, monthly, annualLiving, totalLiving, tuitionTotal, oneTimeTotal, emergency, total };
}

export function computeFundingGap(grossTotal, confirmedBenefits = 0) {
  return Math.max(0, grossTotal - confirmedBenefits);
}

// ---------- TEST SCORING ----------
export function scoreTest(questions, answers) {
  let correct = 0;
  const bySection = {};
  questions.forEach((q) => {
    const sec = q.section;
    if (!bySection[sec]) bySection[sec] = { correct: 0, total: 0 };
    bySection[sec].total += 1;
    if (answers[q.id] === q.answer) {
      correct += 1;
      bySection[sec].correct += 1;
    }
  });
  const score = clamp((correct / questions.length) * 100);
  const skillProfile = Object.entries(bySection).map(([section, v]) => ({
    section, pct: clamp((v.correct / v.total) * 100),
  }));
  const weakest = [...skillProfile].sort((a, b) => a.pct - b.pct).slice(0, 2);
  return { score, correct, total: questions.length, skillProfile, weakest };
}

export const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr) - new Date()) / 86400000);
};
